<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Close, Loading, Promotion } from '@element-plus/icons-vue'
import { useAuthStore } from '@/stores/authStore'
import { useConversationStore } from '@/stores/conversationStore'
import { useErrorAssistStore } from '@/stores/errorAssistStore'
import { useConversationPolling } from '@/composables/useConversationPolling'
import ChatMessages from '@/components/ChatMessages.vue'
import ChatInput from '@/components/ChatInput.vue'
import { ConversationAgent } from '@/types/conversation.type'
import { buildSalesAgentSceneContext } from '@/types/scene-context.type'

const route = useRoute()
const authStore = useAuthStore()
const conversationStore = useConversationStore()
const errorAssistStore = useErrorAssistStore()

const enabled = computed(
  () => authStore.is_loggedin && !!route.meta.requiredAuth && !route.meta.adminPage,
)

// --- 詰まり検知 ---

const IDLE_MS = 30 * 1000
// AI の返答を読んでいる時間を無操作に数えないよう、チャット入力欄が画面にある間は長く待つ
const IDLE_WITH_CHAT_MS = 3 * 60 * 1000
const IDLE_CHECK_INTERVAL_MS = 5 * 1000
// ChatInput.vue の root。画面が chat を持つかはこれの有無で見る。閉じた el-dialog の中身は
// DOM に残るので、描画されているものだけを数える
const CHAT_INPUT_SELECTOR = '.message-input-container'

function hasVisibleChatInput(): boolean {
  return [...document.querySelectorAll(CHAT_INPUT_SELECTOR)].some(
    (el) => el.getClientRects().length > 0,
  )
}

const DEAD_TAP_COUNT = 3
const DEAD_TAP_WINDOW_MS = 15 * 1000
const TAP_MAX_MOVE_PX = 10
const TAP_MAX_DURATION_MS = 500
// tap への反応が描画に出るまでの猶予。伸ばすほど無関係な更新を反応と誤認する
const TAP_RESPONSE_WINDOW_MS = 400
// これより長い text は要素ではなく画面全体を掴んでいるので、文言として使わない
const TAP_LABEL_MAX_LENGTH = 60

const StuckReason = {
  idle: 'idle',
  deadTap: 'deadTap',
} as const
type StuckReason = (typeof StuckReason)[keyof typeof StuckReason]

type Detected = { reason: StuckReason; observed: string }

let lastActivityAt = Date.now()
let idleDismissed = false
let pointerDownAt: { x: number; y: number; at: number } | null = null
let deadTapTimes: number[] = []
let stopTapWatch: (() => void) | null = null
let settleTapWatch: (() => void) | null = null

function resetDetection() {
  lastActivityAt = Date.now()
  idleDismissed = false
  deadTapTimes = []
  stopTapWatch?.()
}

function handleActivity() {
  lastActivityAt = Date.now()
}

function checkIdle() {
  if (!enabled.value || !detecting.value || idleDismissed) return
  if (document.visibilityState !== 'visible') return
  const idleMs = hasVisibleChatInput() ? IDLE_WITH_CHAT_MS : IDLE_MS
  if (Date.now() - lastActivityAt < idleMs) return
  ask.value = {
    phase: AskPhase.detected,
    detected: {
      reason: StuckReason.idle,
      observed: `No activity on this screen for about ${idleMs / 1000} seconds.`,
    },
  }
}

function describeTapTarget(target: EventTarget | null): string | undefined {
  if (!(target instanceof Element)) return undefined
  const text = target.textContent?.replace(/\s+/g, ' ').trim()
  return text && text.length <= TAP_LABEL_MAX_LENGTH ? text : undefined
}

function handlePointerDown(event: PointerEvent) {
  lastActivityAt = Date.now()
  pointerDownAt = { x: event.clientX, y: event.clientY, at: lastActivityAt }
}

// iOS Safari は click 可能でない要素に click を配送しないため、tap は pointer の押下と解放で判定する
function handlePointerUp(event: PointerEvent) {
  const down = pointerDownAt
  pointerDownAt = null
  if (!down || !enabled.value || !detecting.value) return
  const tappedAt = Date.now()
  if (tappedAt - down.at > TAP_MAX_DURATION_MS) return
  if (Math.hypot(event.clientX - down.x, event.clientY - down.y) > TAP_MAX_MOVE_PX) return
  watchTapResponse(event.target, tappedAt)
}

// 反応する要素を selector で列挙する方式は新しい UI 部品のたびに漏れるため、
// tap 後に画面が動いたかを実測する
function watchTapResponse(target: EventTarget | null, tappedAt: number) {
  // 連打では次の tap が猶予より早く来るので、前の tap はその時点までの観測で確定する
  settleTapWatch?.()

  const label = describeTapTarget(target)
  const focusBefore = document.activeElement
  let mutated = false

  const observer = new MutationObserver(() => {
    mutated = true
  })
  observer.observe(document, {
    subtree: true,
    childList: true,
    attributes: true,
    characterData: true,
  })

  function stop() {
    observer.disconnect()
    window.clearTimeout(timer)
    stopTapWatch = null
    settleTapWatch = null
  }

  function settle() {
    stop()
    const focusAfter = document.activeElement
    const focusMoved =
      focusAfter !== focusBefore && focusAfter !== null && focusAfter !== document.body
    // 入力中の欄の中を叩いた（caret 移動）と、文字列を選択した tap は DOM を変えないが反応している
    const tappedInFocused =
      focusAfter !== null &&
      focusAfter !== document.body &&
      target instanceof Node &&
      focusAfter.contains(target)
    const selected = document.getSelection()?.isCollapsed === false
    if (mutated || focusMoved || tappedInFocused || selected) {
      deadTapTimes = []
      return
    }
    deadTapTimes = [...deadTapTimes.filter((t) => tappedAt - t <= DEAD_TAP_WINDOW_MS), tappedAt]
    if (deadTapTimes.length < DEAD_TAP_COUNT) return
    const count = deadTapTimes.length
    ask.value = {
      phase: AskPhase.detected,
      detected: {
        reason: StuckReason.deadTap,
        observed: label
          ? `Tapped "${label}" ${count} times, but nothing responded.`
          : `Tapped the screen ${count} times, but nothing responded.`,
      },
    }
  }

  const timer = window.setTimeout(settle, TAP_RESPONSE_WINDOW_MS)
  stopTapWatch = stop
  settleTapWatch = settle
}

// アプリを離れて戻った直後を無操作とみなさない
function handleVisibilityChange() {
  if (document.visibilityState === 'visible') lastActivityAt = Date.now()
}

const ACTIVITY_EVENTS = ['keydown', 'wheel', 'touchmove', 'scroll'] as const
const listenerOptions: AddEventListenerOptions = { capture: true, passive: true }
let idleTimerId: number | null = null

onMounted(() => {
  ACTIVITY_EVENTS.forEach((type) =>
    document.addEventListener(type, handleActivity, listenerOptions),
  )
  document.addEventListener('pointerdown', handlePointerDown, listenerOptions)
  document.addEventListener('pointerup', handlePointerUp, listenerOptions)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  idleTimerId = window.setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS)
})

onUnmounted(() => {
  ACTIVITY_EVENTS.forEach((type) =>
    document.removeEventListener(type, handleActivity, listenerOptions),
  )
  document.removeEventListener('pointerdown', handlePointerDown, listenerOptions)
  document.removeEventListener('pointerup', handlePointerUp, listenerOptions)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (idleTimerId !== null) window.clearInterval(idleTimerId)
  stopTapWatch?.()
})

// --- 相談の下書きと送信 ---

const { startPolling, stopPolling } = useConversationPolling()

const AskPhase = {
  none: 'none',
  detected: 'detected',
  sending: 'sending',
  posted: 'posted',
} as const
type AskPhase = (typeof AskPhase)[keyof typeof AskPhase]

const SUGGESTED_QUESTION = "I'm stuck on this screen and not sure what to do next."

// phase と独立に持つと無効な組み合わせが生まれ、各所に guard が要るため 1 つの union にする
type AskState =
  | { phase: typeof AskPhase.none }
  | { phase: typeof AskPhase.detected; detected: Detected }
  | { phase: typeof AskPhase.sending }
  | { phase: typeof AskPhase.posted; conversationId: number; lastMessageId: number }

const ask = shallowRef<AskState>({ phase: AskPhase.none })
const detecting = computed(() => ask.value.phase === AskPhase.none)

// 詰まりはどの画面でも起こるので、専用の会話でなく開いている salesAgent の会話に文脈ごと乗せる。
// currentConversation は各画面が差し替えるため、投稿先と一致している間だけ扱う
const conversation = computed(() => {
  const current = conversationStore.currentConversation
  const state = ask.value
  if (!current) return null
  if (state.phase === AskPhase.detected || state.phase === AskPhase.sending) {
    return current.agent === ConversationAgent.salesAgent ? current : null
  }
  if (state.phase === AskPhase.posted) {
    return current.id === state.conversationId ? current : null
  }
  return null
})
const messages = computed(() => conversation.value?.chatMessages ?? [])
// store の activeTx は画面が替わっても残るので、投稿先の会話のものだけを見る
const activeTx = computed(() => {
  const tx = conversationStore.activeTx
  return tx && tx.conversationId === conversation.value?.id ? tx : null
})
const isAgentWorking = computed(() => {
  const status = activeTx.value?.status
  return status === 'queued' || status === 'in_progress'
})

const isWorking = computed(
  () =>
    ask.value.phase === AskPhase.sending ||
    (ask.value.phase === AskPhase.posted && isAgentWorking.value),
)
const isAnswerReady = computed(() => {
  const state = ask.value
  if (state.phase !== AskPhase.posted || isAgentWorking.value) return false
  return messages.value.some(
    (m) => m.id > state.lastMessageId && m.role === 'assistant' && m.text.trim() !== '',
  )
})

function composeQuestion(text: string, observed: string): string {
  return [
    text,
    '',
    '--- Context (captured automatically) ---',
    `Screen: ${route.path}`,
    `Observed: ${observed}`,
  ].join('\n')
}

function sendQuestion(text: string, detected: Detected) {
  ask.value = { phase: AskPhase.sending }
  dismissedWhileWorking.value = false
  const existing = conversation.value
  const conversationIdPromise = existing
    ? Promise.resolve(existing.id)
    : conversationStore.start(`Help on ${route.path}`, ConversationAgent.salesAgent)
  return conversationIdPromise
    .then((id) =>
      conversationStore
        .postChatMessage(composeQuestion(text, detected.observed), id, undefined, {
          sceneContext: buildSalesAgentSceneContext(),
        })
        .then(({ tx, messages: posted }) => {
          // 送信中に画面が変わっていたら破棄済み。投稿は会話に残るので追わない
          if (ask.value.phase !== AskPhase.sending) return
          ask.value = {
            phase: AskPhase.posted,
            conversationId: id,
            lastMessageId: Math.max(0, ...posted.map((m) => m.id)),
          }
          startPolling(tx.conversationId, true)
        }),
    )
    .catch((error) => {
      if (ask.value.phase !== AskPhase.sending) return
      ask.value = { phase: AskPhase.detected, detected }
      throw error
    })
}

function sendFollowUp(text: string, conversationId: number) {
  inputText.value = ''
  conversationStore
    .postChatMessage(text, conversationId, undefined, {
      sceneContext: buildSalesAgentSceneContext(),
    })
    .then(({ tx }) => {
      startPolling(tx.conversationId, true)
    })
    .catch((error) => {
      console.error('Failed to send follow-up:', error)
    })
}

// --- バナー表示 ---

const dismissedWhileWorking = ref(false)

// API エラーのバナーと同じ位置に出るため、あちらが出ている間は譲る
const errorAssistBusy = computed(() => errorAssistStore.askedEntry !== null)

const bannerVisible = computed(
  () =>
    (ask.value.phase === AskPhase.detected ||
      ask.value.phase === AskPhase.sending ||
      ask.value.phase === AskPhase.posted) &&
    enabled.value &&
    !sheetOpen.value &&
    !errorAssistBusy.value &&
    !(dismissedWhileWorking.value && isWorking.value),
)

function discard() {
  stopPolling()
  ask.value = { phase: AskPhase.none }
  dismissedWhileWorking.value = false
  sheetOpen.value = false
  inputText.value = ''
  resetDetection()
}

// 相談前の × は兆候の取り消し。無操作は同じ画面に居る間は出し直さない（読んでいるだけの
// 可能性が高い）が、連打は改めて数える
function handleDismissBanner() {
  const state = ask.value
  if (state.phase === AskPhase.detected) {
    const wasIdle = state.detected.reason === StuckReason.idle
    discard()
    idleDismissed = wasIdle
    return
  }
  if (isWorking.value) {
    dismissedWhileWorking.value = true
    return
  }
  discard()
}

// 画面が変わったら詰まりの兆候は持ち越さない。下書きは検知した画面の事実で組み立てる
// ものなので、送信完了前なら捨てる。投稿済みの会話は画面を跨いで残す
watch([() => route.fullPath, enabled], () => {
  if (!enabled.value || ask.value.phase !== AskPhase.posted) {
    discard()
    return
  }
  resetDetection()
})

// 画面側が別の会話に差し替えたら、相談はその会話（/chat から辿れる）に任せて手放す
watch(conversation, (value) => {
  if (ask.value.phase === AskPhase.posted && value === null) discard()
})

// --- シート ---

const sheetOpen = ref(false)
const sheetBodyRef = ref<HTMLElement | null>(null)
const inputText = ref('')

function handleSend() {
  const text = inputText.value.trim()
  const state = ask.value
  if (!text) return
  if (state.phase === AskPhase.posted) {
    sendFollowUp(text, state.conversationId)
  } else if (state.phase === AskPhase.detected) {
    inputText.value = ''
    sendQuestion(text, state.detected).catch((error) => {
      console.error('Failed to send the question:', error)
      inputText.value = text
    })
  }
}

// 書きかけの文は消さない
function handleSendSuggestion() {
  const state = ask.value
  if (state.phase !== AskPhase.detected) return
  sendQuestion(SUGGESTED_QUESTION, state.detected).catch((error) => {
    console.error('Failed to send the question:', error)
  })
}

// 閉じても相談は破棄しない。送信前の書きかけは Open chat で開き直せるよう残す
function closeSheet() {
  sheetOpen.value = false
  if (ask.value.phase === AskPhase.posted) inputText.value = ''
}

function scrollSheetToBottom() {
  nextTick(() => {
    const el = sheetBodyRef.value
    if (el) el.scrollTop = el.scrollHeight
  })
}

watch([sheetOpen, () => messages.value.length, isAgentWorking], () => {
  if (sheetOpen.value) scrollSheetToBottom()
})
</script>

<template>
  <Transition name="banner-slide">
    <div
      v-if="bannerVisible"
      class="stuck-banner"
      :class="{ 'is-ready': isAnswerReady || ask.phase === AskPhase.detected }"
      role="status"
    >
      <div class="banner-head">
        <button v-if="isWorking" class="banner-progress" @click="sheetOpen = true">
          <el-icon class="is-loading" :size="13"><Loading /></el-icon>
          <span>Helpdesk AI is looking at this screen…</span>
        </button>
        <p v-else-if="ask.phase === AskPhase.detected" class="banner-title">Ask Helpdesk AI</p>
        <p v-else-if="isAnswerReady" class="banner-title">Reply from Helpdesk AI</p>
        <p v-else class="banner-sub">
          Helpdesk AI couldn't reply right now. Please contact support.
        </p>
        <el-button
          class="banner-close"
          :icon="Close"
          size="small"
          circle
          aria-label="Dismiss"
          @click="handleDismissBanner"
        />
      </div>
      <el-button
        v-if="ask.phase === AskPhase.detected || isAnswerReady"
        class="banner-action"
        type="primary"
        size="small"
        @click="sheetOpen = true"
      >
        Open chat
      </el-button>
    </div>
  </Transition>

  <Teleport to="body">
    <Transition name="fade">
      <div v-if="sheetOpen" class="sheet-overlay" @click.self="closeSheet">
        <Transition name="sheet-slide" appear>
          <div class="assist-sheet" role="dialog" aria-label="Helpdesk AI for this screen">
            <header class="sheet-header">
              <div class="sheet-heading">
                <p class="sheet-title">Helpdesk AI</p>
                <p class="sheet-sub">
                  {{
                    ask.phase === AskPhase.posted
                      ? 'Help with this screen'
                      : 'Details about this screen will be attached.'
                  }}
                </p>
              </div>
              <el-button :icon="Close" circle aria-label="Close" @click="closeSheet" />
            </header>

            <div ref="sheetBodyRef" class="sheet-body">
              <ChatMessages :messages="messages" :active-tx="activeTx" />
            </div>

            <div class="sheet-footer">
              <button
                v-if="ask.phase !== AskPhase.posted"
                class="suggestion-chip"
                :disabled="ask.phase === AskPhase.sending"
                @click="handleSendSuggestion"
              >
                <span>{{ SUGGESTED_QUESTION }}</span>
                <el-icon :size="14"><Promotion /></el-icon>
              </button>
              <ChatInput
                v-model="inputText"
                :placeholder="
                  ask.phase === AskPhase.posted
                    ? 'Ask a follow-up...'
                    : 'What are you trying to do?'
                "
                @send="handleSend"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.stuck-banner {
  position: fixed;
  right: 12px;
  /* footer を持つ画面ではその上に逃がす。--app-bottom-inset は useBottomInset が view 側から公開する */
  bottom: max(16px, env(safe-area-inset-bottom), calc(var(--app-bottom-inset, 0px) + 12px));
  z-index: var(--z-takeover);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  max-width: min(480px, calc(100vw - 24px));
  padding: 10px 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid var(--el-border-color);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &.is-ready {
    border-color: var(--el-color-primary);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
  }

  .banner-head {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .banner-close,
  .banner-action {
    flex-shrink: 0;
  }

  .banner-title,
  .banner-sub,
  .banner-progress {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    font-size: var(--font-size-caption);
    line-height: var(--line-height-tight);
  }

  .banner-title {
    font-weight: bold;
  }

  .banner-progress {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    opacity: 0.55;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-takeover);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}

.assist-sheet {
  display: flex;
  flex-direction: column;
  width: min(560px, 100vw);
  height: 70dvh;
  background: #ffffff;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.2);
  overflow: hidden;

  .sheet-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--el-border-color);
  }

  .sheet-heading {
    flex: 1;
    min-width: 0;
  }

  .sheet-title {
    margin: 0;
    font-size: var(--font-size-body-strong);
    font-weight: bold;
    line-height: var(--line-height-tight);
  }

  .sheet-sub {
    margin: 2px 0 0;
    font-size: var(--font-size-caption);
    line-height: var(--line-height-tight);
    opacity: 0.7;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sheet-body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
    background: #f5f5f5;
  }

  .sheet-footer {
    padding: 8px 12px max(8px, env(safe-area-inset-bottom));
    border-top: 1px solid var(--el-border-color);
  }

  .suggestion-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 8px;
    padding: 8px 12px;
    border: 1px solid var(--el-color-primary);
    border-radius: 12px; /* 2 行以上でも崩れない大きさ */
    background: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font: inherit;
    font-size: var(--font-size-caption);
    line-height: var(--line-height-tight);
    text-align: left;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    span {
      flex: 1 1 auto;
      min-width: 0;
      /* 押す前に文面を全部読めるよう折り返す */
      white-space: normal;
      overflow-wrap: anywhere;
    }

    .el-icon {
      flex-shrink: 0;
    }
  }
}

.banner-slide-enter-active,
.banner-slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.banner-slide-enter-from,
.banner-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 0.24s ease;
}
.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateY(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
